'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { trpcClient } from '@/lib/trpc'
import { CheckCircle2, Database, Loader2, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

type CacheJob = {
  id: string
  label: string
  description: string
  status: 'idle' | 'running' | 'success' | 'error'
  result?: string
  lastRun?: string
}

export function CacheSection() {
  const [jobs, setJobs] = useState<CacheJob[]>([
    {
      id: 'unified-cache',
      label: 'Unified Cache + GRPO',
      description: 'Updates all game stats, demo games, GRPO games, model winning rates, and head-to-head pairs.',
      status: 'idle',
    },
    {
      id: 'user-languages',
      label: 'User Languages',
      description: 'Updates each user\'s language preference based on their most recent game.',
      status: 'idle',
    },
  ])
  const [lastUpdated, setLastUpdated] = useState<string | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(true)

  const fetchLastUpdated = useCallback(async () => {
    setLoadingStatus(true)
    try {
      const result = await trpcClient.queryRouter.getAllGameStats.query()
      if (result.lastUpdated) {
        setLastUpdated(result.lastUpdated)
      }
    }
    catch {
      // ignore
    }
    finally {
      setLoadingStatus(false)
    }
  }, [])

  useEffect(() => {
    fetchLastUpdated()
  }, [fetchLastUpdated])

  const updateJob = (jobId: string, update: Partial<CacheJob>) => {
    setJobs(prev => prev.map(j => j.id === jobId ? { ...j, ...update } : j))
  }

  const runUnifiedCache = async () => {
    updateJob('unified-cache', { status: 'running', result: undefined })
    try {
      const result = await trpcClient.queryRouter.refreshAllCache.mutate()
      updateJob('unified-cache', {
        status: 'success',
        result: `Cache refreshed. Timestamp: ${result.timestamp}`,
        lastRun: new Date().toISOString(),
      })
      setLastUpdated(result.timestamp)
    }
    catch (error) {
      updateJob('unified-cache', {
        status: 'error',
        result: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      })
    }
  }

  const runUserLanguages = async () => {
    updateJob('user-languages', { status: 'running', result: undefined })
    try {
      const result = await trpcClient.batchRouter.updateUserLanguages.mutate()
      const breakdown = Object.entries(result.details.languageBreakdown)
        .map(([lang, count]) => `${lang}: ${count}`)
        .join(', ')
      updateJob('user-languages', {
        status: 'success',
        result: `Updated ${result.details.totalUsers} users. Breakdown: ${breakdown}`,
        lastRun: new Date().toISOString(),
      })
    }
    catch (error) {
      updateJob('user-languages', {
        status: 'error',
        result: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      })
    }
  }

  const runAll = async () => {
    await Promise.all([runUnifiedCache(), runUserLanguages()])
  }

  const runJob = (jobId: string) => {
    switch (jobId) {
      case 'unified-cache': { return runUnifiedCache()
      }
      case 'user-languages': { return runUserLanguages()
      }
      default: { break
      }
    }
  }

  const isAnyRunning = jobs.some(j => j.status === 'running')

  return (
    <div className="bg-white px-4 py-8 dark:bg-slate-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-foreground md:text-2xl lg:text-3xl">
            Cache Management
          </h2>
          <p className="text-sm text-muted-foreground">
            Manually trigger cache cron jobs and view their results.
          </p>
          {lastUpdated && !loadingStatus && (
            <p className="mt-1 text-xs text-muted-foreground">
              Last cache update: {new Date(lastUpdated).toLocaleString()}
            </p>
          )}
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Database className="size-5" />
                Cache Jobs
              </CardTitle>
              <Button
                variant="default"
                size="sm"
                onClick={runAll}
                disabled={isAnyRunning}
              >
                {isAnyRunning ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 size-4" />
                    Run All
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {jobs.map(job => (
                <div
                  key={job.id}
                  className="rounded-lg border border-border p-4 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-foreground">{job.label}</h3>
                        {job.status === 'success' && (
                          <CheckCircle2 className="size-4 text-emerald-500" />
                        )}
                        {job.status === 'error' && (
                          <span className="text-xs text-destructive">✗ Failed</span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">{job.description}</p>
                      {job.result && (
                        <p className={`mt-2 rounded-md p-2 text-xs ${
                          job.status === 'error'
                            ? 'bg-destructive/10 text-destructive'
                            : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                        }`}
                        >
                          {job.result}
                        </p>
                      )}
                      {job.lastRun && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Last run: {new Date(job.lastRun).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => runJob(job.id)}
                      disabled={job.status === 'running' || isAnyRunning}
                    >
                      {job.status === 'running' ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <RefreshCw className="size-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
